import { archiveTemplate, getTemplates } from './template-service';
import { APSProgramsList } from './APSProgramsList';
import { getDateFilterProps, getTextFilterProps } from '../utils';
import { TemplateViewModel } from '@/app/features/dashboard/jobs-list/models';
import {
  modalUtils,
  hasPermissionToRender,
  toSafeArray,
} from '@/app/core/utils';
import { JobsInfoButton } from '@/app/features/dashboard/jobs-list/JobsInfoButton';
import { getCurrentFeatureCode } from '@/app/core/auth';
import { Key, useEffect, useState, useRef, useCallback, Fragment } from 'react';
import { Table } from 'antd';
import {
  Text,
  Flex,
  Button,
  Icon,
  HStack,
  VStack,
  Box,
} from '@chakra-ui/react';
import { Modal, RadioGroup, getRandomNumbers } from '@ssar-app/uikit';
import {
  DashboardPermissions,
  Feature,
  Job,
  Template,
  TemplatesPermissions,
  TemplatesSearchResponse,
} from '@ssar-app/domain';
import dayjs from 'dayjs';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import {
  PaginationConfig,
  SearchRequest,
  SortConfig,
  SortDirection,
} from '@ssar-app/domain/src/models/search-models';
import { FaRedo } from 'react-icons/fa/index.js';
import {
  AiOutlineDown,
  AiOutlineExclamationCircle,
  AiOutlineRight,
} from 'react-icons/ai/index.js';

interface TemplatesListProps {
  activeTab: string;
  feature: Feature;
}

export const TemplatesList = (props: TemplatesListProps) => {
  const { activeTab, feature } = props;
  const [templates, setTemplates] = useState<TemplateViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState('All');
  const pagination = useRef<TablePaginationConfig>({
    hideOnSinglePage: false,
    defaultCurrent: 1,
    current: 1,
    total: 0,
  });
  const [templatesCount, setTemplatesCount] = useState({
    total: 0,
    active: 0,
    archived: 0,
  });

  const filtersPassed = useRef<Record<string, (Key | boolean)[] | null>>({});
  const sorterPassed = useRef<
    SorterResult<TemplateViewModel> | SorterResult<TemplateViewModel>[]
  >([]);

  const canViewTemplates = hasPermissionToRender(
    getCurrentFeatureCode(),
    TemplatesPermissions.MayViewTemplates,
  );

  const canArchiveTemplate = hasPermissionToRender(
    getCurrentFeatureCode(),
    TemplatesPermissions.MayArchiveTemplates,
  );

  const isAdmin = hasPermissionToRender(
    getCurrentFeatureCode(),
    DashboardPermissions.MayViewAdminColumns,
  );

  const columns = [
    {
      title: 'Template Id',
      align: 'center' as const,
      dataIndex: 'templateId',
      sorter: true,
      ...(canViewTemplates ? getTextFilterProps('Template Id') : {}),
      render: (text: string, _record: Template) => {
        return <>{text.toUpperCase()}</>;
      },
    },
    {
      title: 'Template Name',
      dataIndex: 'name',
      sorter: true,
      ...(canViewTemplates ? getTextFilterProps('Template Name') : {}),
      render: (text: string, _record: Template) => {
        return <>{text}</>;
      },
    },
    {
      title: 'Program Name',
      dataIndex: ['program', 'name'],
      sorter: true,
      ...(canViewTemplates ? getTextFilterProps('Program Name') : {}),
      render: (text: string, record: Template) => {
        return (
          <HStack>
            <JobsInfoButton
              record={
                {
                  programId: record.program.id,
                  programName: record.program.name,
                } as Job
              }
              setIsLoading={setIsLoading}
              forJobs={false}
            />
            <Text>{text}</Text>
          </HStack>
        );
      },
    },
    isAdmin
      ? {
          title: 'Subscriber Code',
          align: 'center' as const,
          dataIndex: 'subCode',
          sorter: true,
          ...(canViewTemplates ? getTextFilterProps('Subscriber Code') : {}),
        }
      : null,
    {
      title: 'Created By',
      align: 'center' as const,
      dataIndex: 'userId',
      sorter: true,
      ...(canViewTemplates ? getTextFilterProps('Created By') : {}),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string) => {
        return text ? dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ') : '';
      },
    },
    {
      title: 'Updated Date',
      dataIndex: 'lastUpdatedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string) => {
        return text ? dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ') : '';
      },
    },
    // {
    //   title: 'Expiry Date',
    //   dataIndex: 'expiredDate',
    //   sorter: true,
    //   ...getDateFilterProps(),
    //   render: (text: string) => {
    //     return text ? dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ') : '';
    //   },
    // },
    canArchiveTemplate
      ? {
          title: 'Actions',
          align: 'center' as const,
          render: (_text: string, row: Template) => {
            return (
              <Button
                disabled={row.archived}
                onClick={() => confirmConvertToArchive(row)}
                colorScheme='gray'
                variant={'outline'}
                size='sm'
              >
                {row.archived ? 'Archived' : 'Archive'}
              </Button>
            );
          },
        }
      : null,
  ].filter((a) => a !== null);

  // const onSelectChange = (selectedRowKeys: (string | number)[]): void => {
  //   // console.log('selectedRowKeys changed: ', selectedRowKeys);
  //   setselectedTemplates(selectedRowKeys.map((a) => a.toString()));
  // };

  // const rowSelection = {
  //   selectedRowKeys: selectedTemplates.map((a) => a as string | number),
  //   onChange: onSelectChange,
  //   preserveSelectedRowKeys: true,
  // };

  const fetchData = (
    paginationInput: TablePaginationConfig = pagination.current,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter:
      | SorterResult<TemplateViewModel>
      | SorterResult<TemplateViewModel>[] = [],
  ): void => {
    setIsLoading(true);

    filtersPassed.current = { status: [tabValue], ...filters };
    sorterPassed.current = sorter;

    const paginationConfig: PaginationConfig = {
      pageSize: paginationInput?.pageSize || 10,
      current: paginationInput?.current || 1,
    };

    const sortConfig: SortConfig[] = [];

    toSafeArray(sorter).forEach((a) => {
      if (a.field) {
        const dir =
          a.order === 'descend'
            ? SortDirection.Descending
            : a.order === 'ascend'
            ? SortDirection.Ascending
            : null;
        if (dir) {
          sortConfig.push({
            field: a.field.toString(),
            direction: dir,
          });
        }
      }
    });
    // console.log(_filters);
    const searchModel: SearchRequest = {
      paging: paginationConfig,
      sortBy: sortConfig,
      filter: {
        ...filtersPassed.current,
        product: [feature?.code],
        status: [tabValue],
        ...filters,
      },
    };

    getTemplates(searchModel)
      .then((templatesResponse: TemplatesSearchResponse) => {
        const { paging, data, templatesCount } = templatesResponse;
        setTemplatesCount(templatesCount);
        const mergedPagination = {
          ...pagination.current,
          ...paging,
        };
        mergedPagination.current = +mergedPagination.current;
        mergedPagination.pageSize = +mergedPagination.pageSize;
        pagination.current = mergedPagination;
        setTemplates(data);
      })
      .catch((err) => {
        console.error(err);
        modalUtils.showError(err, 'Error while getting Templates');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const confirmConvertToArchive = (record: Template) => {
    Modal.confirm({
      header: 'Confirmation',
      icon: <Icon as={AiOutlineExclamationCircle} />,
      content: `You are about to archive '${record.name}'. Do you want to continue?`,
      onOk() {
        convertToArchive(record);
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel() {},
    });
  };

  const convertToArchive = (record: Template) => {
    setIsLoading(true);
    archiveTemplate(record.templateId)
      .then((result) => {
        setIsLoading(false);
        if (result?.resultCode === 0) {
          fetchData(
            pagination.current,
            filtersPassed.current,
            sorterPassed.current,
          );
          modalUtils.showSuccessMessage(result.messages.join('\n'));
        } else {
          modalUtils.showError(result, 'Error while deleting Template');
        }
      })
      .catch((err) => {
        modalUtils.showError(err, 'Error while deleting Template');
      })
      .finally(() => {
        setIsLoading(false);
        setTabValue('Archived');
        filtersPassed.current = {
          ...filtersPassed.current,
          status: ['Archived'],
        };
        fetchData(
          pagination.current,
          filtersPassed.current,
          sorterPassed.current,
        );
      });
  };

  const onChangeTab = (arg0: string) => {
    setTabValue(arg0);
    filtersPassed.current = {
      ...filtersPassed.current,
      ...{
        status: [arg0],
      },
    };
    fetchData({}, filtersPassed.current, []);
  };
  const fetchDataWrapper = useCallback(
    () => fetchData(pagination.current, {}, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [key, setKey] = useState(getRandomNumbers(1)[0]);

  useEffect(() => {
    activeTab === 'templates' &&
      fetchData(
        pagination.current,
        filtersPassed.current,
        sorterPassed.current,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div>
      {templates && canViewTemplates && (pagination.current?.total || 1) > 0 ? (
        <Fragment key={key}>
          <Flex
            justify='space-between'
            className='job-list-commands-bar'
            sx={{ pb: 2 }}
          >
            <Text className='card-title' fontSize='lg' textColor='black'>
              Templates Summary
            </Text>
            <Box>
              <Button
                colorScheme='gray'
                size='sm'
                variant={'outline'}
                onClick={() => {
                  setTabValue('All');
                  setKey(getRandomNumbers(1)[0]);
                  fetchDataWrapper();
                }}
                leftIcon={<FaRedo />}
              >
                {'Reset & Refresh'}
              </Button>
            </Box>
          </Flex>
          <Flex justify='space-between' mb={4}>
            <RadioGroup
              defaultValue='All'
              value={tabValue}
              variant='solid'
              optionsType='button'
              radioButtonProps={{ shadow: 'none', borderRadius: 'none' }}
              onChange={(e: string) => {
                onChangeTab(e);
              }}
              options={[
                {
                  label: `All Templates (${templatesCount.total})`,
                  value: 'All',
                },
                {
                  label: `Active (${templatesCount.active})`,
                  value: 'Active',
                },
                {
                  label: `Archived (${templatesCount.archived})`,
                  value: 'Archived',
                },
              ]}
              applyBorderRadiusForStartEndButtons={true}
              borderRadiusForStartEndButtons={5}
            />
          </Flex>
          <Table
            onChange={fetchData}
            showSorterTooltip={false}
            className='table-data-list'
            size='small'
            bordered={true}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rowKey={(row) => row.templateId || (row as any)['_id']}
            loading={isLoading}
            // rowSelection={rowSelection}
            dataSource={templates}
            columns={columns}
            pagination={{
              ...pagination.current,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomLeft'],
              showSizeChanger: true,
              simple: false,
            }}
            expandable={{
              expandedRowClassName: () => 'templates-expanded-row',
              columnWidth: '60px',
              expandIcon: ({ expanded, onExpand, record }) => {
                const programsCount = record?.programsCount || 0;
                return (
                  <HStack
                    border={'1px'}
                    borderRadius={12}
                    pl={1}
                    pr={1}
                    onClick={(e) => onExpand(record, e)}
                  >
                    <Text w={4}>
                      {programsCount < 10 && programsCount > 0
                        ? '0' + programsCount
                        : programsCount}
                    </Text>
                    {expanded ? (
                      <Icon as={AiOutlineDown} />
                    ) : (
                      <Icon as={AiOutlineRight} />
                    )}
                  </HStack>
                );
              },
              expandedRowRender: (record) => (
                <VStack>
                  <Text
                    w={'full'}
                    borderBottom={
                      (record?.programsCount || 0) > 0
                        ? '1px solid #B2B6BB'
                        : ''
                    }
                    p={2}
                  >
                    {record.description ? (
                      <>Template Description - {record.description}</>
                    ) : (
                      <Text color={'gray'}>No Description Added</Text>
                    )}
                  </Text>
                  {(record?.programsCount || 0) > 0 && (
                    <APSProgramsList templateId={record.templateId} />
                  )}
                </VStack>
              ),
            }}
          ></Table>
        </Fragment>
      ) : (
        <div>No Permission.</div>
      )}
    </div>
  );
};
